import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  CodeBracketIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  RectangleStackIcon,
  ShieldExclamationIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import useTranslation from '../services/translationService';
import { useNotification } from './NotificationContext';
import { fetchLimitEvents } from '../services/limitEventsService';
import { formatCurrency } from '../utils/currency';
import { getTypeColor, getTypeIcon, getTypeLabel } from '../utils/limitScopes';
import {
  BREACH_TYPES,
  LIMIT_SOURCES,
  EVENT_PROVIDERS,
  PRODUCTS,
  clusterEventsByTarget,
  describeAmounts,
  describeEventScope,
  getBreachMeta,
  summarizeCluster,
  getSourceMeta,
  getProviderLabel,
  isFixableInPanel,
  formatDayHeading,
  formatDateTime,
  formatClock,
  timeAgo
} from '../utils/limitEvents';

/**
 * Limit history — every stake the casino refused for hitting a limit.
 *
 * The rows are written by the wallet at the moment of the refusal and already
 * carry their own prose, so this screen's job is not to explain a refusal from
 * scratch. It is to make a pile of them navigable on a phone: cut the list down
 * to the ones that matter, group them so a pattern is visible, and put the
 * limit that caused each one a single tap away from being changed.
 */

const RANGES = [
  { id: 'today', days: 1, labelKey: 'limitHistory.rangeToday', labelDefault: 'Today' },
  { id: 'week', days: 7, labelKey: 'limitHistory.rangeWeek', labelDefault: '7 days' },
  { id: 'month', days: 30, labelKey: 'limitHistory.rangeMonth', labelDefault: '30 days' }
];

const GROUPINGS = [
  { id: 'time', labelKey: 'limitHistory.groupTime', labelDefault: 'Time', Icon: ClockIcon },
  { id: 'scope', labelKey: 'limitHistory.groupScope', labelDefault: 'Game', Icon: CubeIcon },
  { id: 'player', labelKey: 'limitHistory.groupPlayer', labelDefault: 'Player', Icon: UserIcon },
  { id: 'owner', labelKey: 'limitHistory.groupOwner', labelDefault: 'Limit', Icon: ShieldExclamationIcon },
  { id: 'reason', labelKey: 'limitHistory.groupReason', labelDefault: 'Reason', Icon: FunnelIcon }
];

const PAGE_SIZE = 60;

const CURRENCY_ORDER = ['LBP', 'USD', 'TND', 'TRY', 'IQD', 'KWD'];

const sortCurrencies = (codes) =>
  [...codes].sort((a, b) => {
    const indexA = CURRENCY_ORDER.indexOf(a);
    const indexB = CURRENCY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return String(a).localeCompare(String(b));
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

const eventCurrency = (event) => event?.transaction?.currency || null;

const EMPTY_FILTERS = {
  breachType: [],
  limitSource: [],
  product: [],
  provider: [],
  configurable: null
};

const countActiveFilters = (filters) =>
  filters.breachType.length +
  filters.limitSource.length +
  filters.product.length +
  filters.provider.length +
  (filters.configurable === null ? 0 : 1);

/** "system:global-fallback" and "country:countrybeirut" both have to read well. */
const describeOwner = (limitOwner) => {
  const [type, ...rest] = String(limitOwner || '').split(':');
  const name = rest.join(':');
  if (type === 'system') return { name: name || 'in code', type: 'system', isSystem: true };
  return { name: name || 'unknown', type: type || 'unknown', isSystem: false };
};

const amountLabel = (value, currency) =>
  Number.isFinite(Number(value))
    ? formatCurrency(Number(value), currency || 'USD', { compact: false })
    : '—';

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

/** What to call each of the two figures, which depends on the breach. */
const amountLabels = (amounts, t) => ({
  tried: amounts.isRunningTotal
    ? t('limitHistory.wouldBeTotal', {}, 'Total on this match')
    : t('limitHistory.tried', {}, 'Bet attempted'),
  cap: amounts.isTooSmall
    ? t('limitHistory.minimumAllowed', {}, 'Minimum allowed')
    : t('limitHistory.maximumAllowed', {}, 'Limit that stopped it')
});

/**
 * The same two figures drawn to scale. A stake at twice the cap and one at
 * twenty times it are very different problems, and the bars say which without
 * anyone reading a number.
 */
const AmountScale = ({ amounts }) => {
  if (amounts.tried === null || amounts.cap === null) return null;

  const peak = Math.max(amounts.tried, amounts.cap, 1);
  const bar = (key, value, color) => (
    <span key={key} className="block h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <span
        className={`block h-full rounded-full ${color}`}
        style={{ width: `${Math.max((value / peak) * 100, 2)}%` }}
      />
    </span>
  );

  return (
    <span className="block space-y-1">
      {bar('tried', amounts.tried, amounts.isTooSmall ? 'bg-amber-400' : 'bg-red-500')}
      {bar('cap', amounts.cap, 'bg-emerald-500')}
    </span>
  );
};

/**
 * The bet against the limit, side by side. Red is what was asked for, green is
 * what was allowed, everywhere on the screen.
 */
// Spans throughout, not divs: this also renders inside the list row's <button>,
// where a div is not valid content.
const AmountPair = ({ amounts, t, size = 'row' }) => {
  const labels = amountLabels(amounts, t);
  const isDetail = size === 'detail';

  return (
    <span className={`grid grid-cols-2 ${isDetail ? 'gap-3' : 'gap-2'}`}>
      {[
        {
          label: labels.tried,
          value: amounts.tried,
          color: amounts.isTooSmall
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-red-600 dark:text-red-400'
        },
        { label: labels.cap, value: amounts.cap, color: 'text-emerald-600 dark:text-emerald-400' }
      ].map(({ label, value, color }) => (
        <span key={label} className="block min-w-0">
          <span
            className={`block ${isDetail ? 'text-[10px]' : 'text-[9px]'} uppercase tracking-wide text-gray-400 dark:text-gray-500 truncate`}
          >
            {label}
          </span>
          <span
            className={`block ${isDetail ? 'text-base' : 'text-xs'} font-bold tabular-nums truncate ${color}`}
          >
            {amountLabel(value, amounts.currency)}
          </span>
        </span>
      ))}
    </span>
  );
};

/** What kind of rule this was — a game, a provider, a league, everything. */
const KindChip = ({ scope }) => {
  if (!scope) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
      <scope.Icon className={`w-3 h-3 ${scope.color}`} />
      {scope.kindLabel}
    </span>
  );
};

/**
 * Whose limit it was. Colour-coded by account type, the same way the rest of
 * the panel colours a country, a city or an agent.
 */
const OwnerChip = ({ limitOwner, t }) => {
  const owner = describeOwner(limitOwner);

  if (owner.isSystem) {
    return (
      <span
        title={owner.name}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap"
      >
        <CodeBracketIcon className="w-3 h-3" />
        {t('limitHistory.inCode', {}, 'In code')}
      </span>
    );
  }

  const Icon = getTypeIcon(owner.type);
  return (
    <span
      title={`${getTypeLabel(owner.type)} @${owner.name}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold whitespace-nowrap ${getTypeColor(owner.type)}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      <span className="truncate max-w-[120px]">{owner.name}</span>
    </span>
  );
};

/** The catalog emoji when there is one, otherwise the icon for the bucket. */
const ScopeGlyph = ({ scope, className = 'w-4 h-4' }) => {
  if (!scope) return null;
  if (scope.emoji) {
    return <span className="text-base leading-none shrink-0">{scope.emoji}</span>;
  }
  return <scope.Icon className={`${className} ${scope.color} shrink-0`} />;
};

/**
 * One refusal, collapsed. What got limited, who hit it, and by how much —
 * everything else is an icon. The prose the wallet wrote is a tap away, which
 * is the only place it is short enough to be worth reading.
 *
 * `compact` is the row inside an opened stack: the game or player is already
 * on the header, so the list is just when, who (or what), and the two figures.
 */
const EventRow = ({ event, scope, onOpen, t, compact = false, hide = null }) => {
  const breach = getBreachMeta(event);
  const BreachIcon = breach.Icon;
  const product = PRODUCTS[event.transaction?.product];
  const ProductIcon = product?.Icon;
  const fixable = isFixableInPanel(event);
  const owner = describeOwner(event.limitOwner);
  const providerShort = EVENT_PROVIDERS[event.transaction?.provider]?.short;
  const amounts = describeAmounts(event);
  const playerName = event.player?.username || event.username;

  if (compact) {
    const primary =
      hide === 'both'
        ? scope?.detail || null
        : hide === 'player'
          ? scope?.detail || scope?.label || getProviderLabel(event)
          : `@${playerName}`;
    const secondary = hide === 'target' && scope?.detail ? scope.detail : null;

    return (
      <button
        type="button"
        onClick={() => onOpen(event)}
        title={event.headline}
        className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900/40 active:bg-gray-100 dark:active:bg-gray-900/60 transition-colors touch-manipulation"
      >
        <span className="flex items-center gap-2.5">
          <span className="shrink-0 w-[3.25rem] text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
            {formatClock(event.occurredAtMs)}
          </span>

          <span className={`shrink-0 p-1 rounded-md ${breach.chip}`} title={breach.label}>
            <BreachIcon className="w-3.5 h-3.5" />
          </span>

          <span className="flex-1 min-w-0">
            {(primary || secondary) && (
              <span className="flex items-center gap-1.5">
                {primary && (
                  <span className="min-w-0 truncate text-xs font-semibold text-gray-900 dark:text-gray-100">
                    {primary}
                  </span>
                )}
                {secondary && (
                  <span className="min-w-0 truncate text-[11px] text-gray-500 dark:text-gray-400">
                    {secondary}
                  </span>
                )}
              </span>
            )}
            <span className={`${primary || secondary ? 'mt-0.5' : ''} block`}>
              <AmountPair amounts={amounts} t={t} />
            </span>
          </span>

          <span className="shrink-0 flex items-center gap-1.5">
            {ProductIcon && (
              <span title={product.label} className="flex">
                <ProductIcon
                  className={`w-3.5 h-3.5 ${
                    event.transaction?.product === 'sports' ? 'text-emerald-500' : 'text-blue-500'
                  }`}
                />
              </span>
            )}
            {fixable && (
              <span
                title={`${t('limitHistory.canFixHere', {}, 'You can change this limit here')} — ${owner.name}`}
                className="flex"
              >
                <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-emerald-500" />
              </span>
            )}
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(event)}
      title={event.headline}
      className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/40 active:bg-gray-100 dark:active:bg-gray-900/60 transition-colors touch-manipulation"
    >
      <span className="flex items-start gap-2.5">
        <span className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${breach.chip}`} title={breach.label}>
          <BreachIcon className="w-4 h-4" />
        </span>

        <span className="flex-1 min-w-0">
          {/* What got limited, first: it is what the eye is looking for. */}
          <span className="flex items-center gap-1.5">
            <ScopeGlyph scope={scope} />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {scope?.label || getProviderLabel(event)}
            </span>
            <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
              {formatClock(event.occurredAtMs)}
            </span>
          </span>

          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="shrink-0 truncate max-w-[45%]">
              @{playerName}
            </span>
            {scope?.detail && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="flex-1 min-w-0 truncate">{scope.detail}</span>
              </>
            )}
          </span>

          {/* The bet against the limit that stopped it. */}
          <span className="mt-1.5 block">
            <AmountPair amounts={amounts} t={t} />
          </span>

          {/* What kind of rule it was, and whose it was. */}
          <span className="mt-1.5 flex flex-wrap items-center gap-1">
            <KindChip scope={scope} />
            <OwnerChip limitOwner={event.limitOwner} t={t} />

            <span className="ml-auto flex items-center gap-1.5 shrink-0">
              {providerShort && (
                <span
                  className="text-[10px] font-medium text-gray-400 dark:text-gray-500"
                  title={getProviderLabel(event)}
                >
                  {providerShort}
                </span>
              )}
              {/* Icon-only, so the tooltip carries the meaning — and it has to
                  sit on the wrapper, because on an inline <svg> the title
                  attribute does not raise one. */}
              {ProductIcon && (
                <span title={product.label} className="flex">
                  <ProductIcon
                    className={`w-3.5 h-3.5 ${
                      event.transaction?.product === 'sports' ? 'text-emerald-500' : 'text-blue-500'
                    }`}
                  />
                </span>
              )}
              {fixable && (
                <span
                  title={`${t('limitHistory.canFixHere', {}, 'You can change this limit here')} — ${owner.name}`}
                  className="flex"
                >
                  <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-emerald-500" />
                </span>
              )}
            </span>
          </span>
        </span>
      </span>
    </button>
  );
};

const formatSpan = (min, max, currency) => {
  if (min === null && max === null) return null;
  if (min === max) return amountLabel(min, currency);
  return `${amountLabel(min, currency)} – ${amountLabel(max, currency)}`;
};

/**
 * Several refusals of the same game, match or player, collapsed to one header.
 * Opening it is a tight timeline — clock, who (or what), the two figures —
 * so a dozen Sweet Bonanza retries can be scanned instead of re-read.
 */
const ClusterBlock = ({ cluster, scopes, grouping, isOpen, onToggle, onOpenEvent, flatUsers, t }) => {
  const summary = summarizeCluster(cluster.events);
  const headerScope = scopes.get(cluster.events[0]?.eventId);
  const hide =
    cluster.axis === 'player'
      ? grouping === 'scope'
        ? 'both'
        : 'player'
      : 'target';
  const tried = formatSpan(summary.triedMin, summary.triedMax, summary.currency);
  const sameCap = summary.capMin !== null && summary.capMin === summary.capMax;
  const timeLabel =
    summary.oldestMs && summary.newestMs
      ? formatClock(summary.oldestMs) === formatClock(summary.newestMs)
        ? formatClock(summary.newestMs)
        : `${formatClock(summary.oldestMs)}–${formatClock(summary.newestMs)}`
      : null;

  const playerLabel =
    hide === 'player'
      ? null
      : summary.playerCount === 1
        ? `@${summary.players[0]}`
        : summary.playerCount > 1
          ? `${summary.playerCount} ${t('limitHistory.playersAffected', {}, 'players')}`
          : null;

  const playerNode =
    cluster.axis === 'player'
      ? flatUsers.find((user) => user.username === cluster.events[0]?.player?.username)
      : null;
  const PlayerIcon = playerNode ? getTypeIcon(playerNode.type || 'player') : UserIcon;

  return (
    <div className={isOpen ? 'bg-gray-50/80 dark:bg-gray-900/30' : ''}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/40 active:bg-gray-100 dark:active:bg-gray-900/60 transition-colors touch-manipulation"
      >
        <span className="flex items-start gap-2.5">
          <span
            className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${
              cluster.axis === 'player' && playerNode
                ? getTypeColor(playerNode.type || 'player')
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {cluster.axis === 'player' ? (
              <PlayerIcon className="w-4 h-4" />
            ) : headerScope ? (
              <ScopeGlyph scope={headerScope} />
            ) : (
              <RectangleStackIcon className="w-4 h-4" />
            )}
          </span>

          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="flex-1 min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {cluster.label}
              </span>
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 tabular-nums">
                {summary.count}
              </span>
              <ChevronDownIcon
                className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </span>

            <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {playerLabel && <span className="truncate max-w-[40%]">{playerLabel}</span>}
              {playerLabel && tried && <span className="text-gray-300 dark:text-gray-600">·</span>}
              {tried && (
                <span className="tabular-nums">
                  {tried}
                  {sameCap && ` / ${amountLabel(summary.capMin, summary.currency)}`}
                </span>
              )}
              {timeLabel && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="tabular-nums">{timeLabel}</span>
                </>
              )}
            </span>

            {headerScope && cluster.axis !== 'player' && (
              <span className="mt-1.5 flex flex-wrap items-center gap-1">
                <KindChip scope={headerScope} />
                {summary.owner && <OwnerChip limitOwner={summary.owner} t={t} />}
              </span>
            )}
          </span>
        </span>
      </button>

      {isOpen && (
        <div className="ml-3 mb-1 mr-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          {cluster.events.map((event) => (
            <EventRow
              key={event.eventId}
              event={event}
              scope={scopes.get(event.eventId)}
              onOpen={onOpenEvent}
              t={t}
              compact
              hide={hide}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** A labelled block of key/value rows inside the detail sheet. */
const DetailSection = ({ title, rows }) => {
  const visible = rows.filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (visible.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900/50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {title}
      </p>
      <dl className="divide-y divide-gray-100 dark:divide-gray-700">
        {visible.map(([label, value]) => (
          <div key={label} className="flex items-start gap-3 px-3 py-2">
            <dt className="w-1/3 shrink-0 text-[11px] text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="flex-1 min-w-0 text-xs text-gray-900 dark:text-gray-100 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail sheet
// ---------------------------------------------------------------------------

/**
 * Everything the wallet recorded about one refusal, plus the two buttons that
 * turn it into a change: fix the limit where it is set, or write a new one for
 * the player.
 */
const EventDetail = ({ event, scope, flatUsers, onClose, onSetLimit, onOpenUser, t, showNotification }) => {
  const [showRaw, setShowRaw] = useState(false);

  const breach = getBreachMeta(event);
  const BreachIcon = breach.Icon;
  const source = getSourceMeta(event);
  const amounts = describeAmounts(event);
  const limit = event.limit || {};
  const transaction = event.transaction || {};
  const player = event.player || {};
  const owner = describeOwner(event.limitOwner);
  const fixable = isFixableInPanel(event);
  const currency = transaction.currency;

  const ownerNode = flatUsers.find((user) => user.username === limit.ownerUsername);
  const playerNode = flatUsers.find((user) => user.username === player.username);

  const copy = (label, value) => {
    navigator.clipboard?.writeText(value).then(
      () => showNotification('success', t('common.success'), `${label} copied`),
      () => showNotification('error', t('common.error'), 'Could not copy')
    );
  };

  const sportsBet = transaction.sportsBet;
  const matchLabel = sportsBet?.matches?.[0]?.matchLabel || event.exposure?.matchLabel;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-slide-up sm:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start gap-3 shrink-0">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full sm:hidden" />

          <span className={`shrink-0 mt-2 sm:mt-0 p-2 rounded-lg ${breach.chip}`}>
            <BreachIcon className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0 mt-2 sm:mt-0">
            <h3 className="flex items-center gap-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">
              {scope && <ScopeGlyph scope={scope} />}
              <span className="truncate">{scope?.label || breach.label}</span>
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {breach.label} · {formatDateTime(event.occurredAtMs)} · {timeAgo(event.occurredAtMs)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 mt-1 sm:mt-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation"
          >
            <XMarkIcon className="w-6 h-6 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* The five things anyone opens this for: what they were on, what
              they bet, what stopped it, what kind of rule it was, and whose. */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {scope?.detail && (
              <p className="px-3 pt-3 text-xs text-gray-500 dark:text-gray-400 truncate">
                {scope.detail}
              </p>
            )}

            <div className="p-3 space-y-2">
              <AmountPair amounts={amounts} t={t} size="detail" />
              <AmountScale amounts={amounts} />
              {amounts.gap !== null && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {amounts.isTooSmall
                    ? t('limitHistory.shortBy', {}, 'Short by')
                    : t('limitHistory.overBy', {}, 'Over by')}{' '}
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {amountLabel(amounts.gap, amounts.currency)}
                    {amounts.gapPct !== null && ` (${amounts.gapPct}%)`}
                  </span>
                </p>
              )}
            </div>

            <dl className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {[
                [t('limitHistory.category', {}, 'Kind of limit'), <KindChip key="kind" scope={scope} />],
                [t('limitHistory.owner', {}, 'Set by'), <OwnerChip key="owner" limitOwner={event.limitOwner} t={t} />],
                [
                  t('limitHistory.playerLabel', {}, 'Player'),
                  <span key="player" className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    @{player.username}
                  </span>
                ],
                [
                  t('limitHistory.through', {}, 'Through'),
                  <span key="through" className="text-xs text-gray-700 dark:text-gray-300">
                    {getProviderLabel(event)}
                  </span>
                ]
              ].map(([label, value]) => (
                <div key={label} className="flex items-center gap-3 px-3 py-2">
                  <dt className="w-1/3 shrink-0 text-[11px] text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="flex-1 min-w-0">{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* What an admin can do about it. Kept above the raw detail: it is the
              reason someone opened this row. */}
          <section
            className={`rounded-xl border-2 p-3 ${
              fixable
                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/20'
                : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40'
            }`}
          >
            <div className="flex items-start gap-2 mb-3">
              {fixable ? (
                <WrenchScrewdriverIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <CodeBracketIcon className="w-5 h-5 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${fixable ? 'text-emerald-900 dark:text-emerald-100' : 'text-slate-800 dark:text-slate-200'}`}>
                  {fixable
                    ? t('limitHistory.canFixHere', {}, 'You can change this limit here')
                    : t('limitHistory.needsDeploy', {}, 'This cap lives in code')}
                </p>
                <p className={`text-[11px] leading-snug ${fixable ? 'text-emerald-800/80 dark:text-emerald-200/80' : 'text-slate-600 dark:text-slate-400'}`}>
                  {fixable
                    ? `${source?.label || ''} — ${getTypeLabel(owner.type)} "${owner.name}"`
                    : source?.summary || limit.limitSourceDescription}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {fixable && (
                <button
                  type="button"
                  onClick={() => onSetLimit(event, limit.ownerUsername)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm touch-manipulation"
                >
                  <WrenchScrewdriverIcon className="w-4 h-4" />
                  {t('limitHistory.changeThisLimit', {}, 'Change the limit that blocked this')}
                </button>
              )}

              <button
                type="button"
                onClick={() => onSetLimit(event, player.username)}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg transition-colors touch-manipulation ${
                  fixable
                    ? 'bg-white dark:bg-gray-800 text-emerald-700 dark:text-emerald-300 border-2 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm'
                }`}
              >
                <ShieldExclamationIcon className="w-4 h-4" />
                {t('limitHistory.setLimitForPlayer', {}, 'Set a limit for')} @{player.username}
              </button>

              {!fixable && (
                <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                  {t(
                    'limitHistory.hardcodedCaveat',
                    {},
                    'A limit set here can only make this scope tighter. Raising the cap above the one in code needs a deploy.'
                  )}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {playerNode && (
                  <button
                    type="button"
                    onClick={() => onOpenUser(player.username)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 touch-manipulation"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    {t('limitHistory.openPlayerLimits', {}, "Player's limits")}
                  </button>
                )}
                {ownerNode && ownerNode.username !== player.username && (
                  <button
                    type="button"
                    onClick={() => onOpenUser(limit.ownerUsername)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 touch-manipulation"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    @{limit.ownerUsername}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* The prose the wallet wrote at the moment of the refusal. Below the
              facts and the buttons on purpose: it is for the times the numbers
              do not settle it. */}
          <section className="rounded-xl bg-gray-50 dark:bg-gray-900/40 p-3 space-y-2">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed">
              {event.headline}
            </p>
            {event.explanation && (
              <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                {event.explanation}
              </p>
            )}
          </section>

          {/* The decision, step by step. Written by the code that took it, so it
              is the closest thing to a trace an admin will ever get. */}
          {Array.isArray(event.decisionPath) && event.decisionPath.length > 0 && (
            <section>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                {t('limitHistory.stepByStep', {}, 'Step by step')}
              </p>
              <ol className="space-y-2">
                {event.decisionPath.map((step, index) => (
                  <li key={index} className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-300 flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="flex-1 min-w-0 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                      {/* The stored text is already numbered; the badge says it better. */}
                      {String(step).replace(/^\d+\.\s*/, '')}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {event.exposure && (
            <DetailSection
              title={t('limitHistory.matchTotal', {}, 'Running total on this match')}
              rows={[
                [t('limitHistory.match', {}, 'Match'), event.exposure.matchLabel],
                [t('limitHistory.alreadyStaked', {}, 'Already staked'), amountLabel(event.exposure.alreadyStaked, currency)],
                [t('limitHistory.thisBet', {}, 'This bet'), amountLabel(event.exposure.attemptedStake, currency)],
                [t('limitHistory.wouldBe', {}, 'Would have been'), amountLabel(event.exposure.wouldBeTotal, currency)],
                [t('limitHistory.allowedTotal', {}, 'Allowed total'), amountLabel(event.exposure.maxTotal, currency)],
                [t('limitHistory.remaining', {}, 'Left to stake'), amountLabel(event.exposure.remainingAllowance, currency)]
              ]}
            />
          )}

          {/* The rest of the record. Anything already on the facts card at the
              top is left out rather than said twice. */}
          <DetailSection
            title={t('limitHistory.theBet', {}, 'The bet')}
            rows={[
              [t('limitHistory.game', {}, 'Game'), transaction.gameName || transaction.gameId],
              [t('limitHistory.match', {}, 'Match'), matchLabel],
              [t('limitHistory.competition', {}, 'Competition'), sportsBet?.competitions?.join(', ')],
              [t('limitHistory.markets', {}, 'Markets'), sportsBet?.markets?.join(', ')],
              [t('limitHistory.legs', {}, 'Legs'), sportsBet?.legCount],
              [t('limitHistory.stake', {}, 'Stake'), amountLabel(transaction.requestedAmount, currency)],
              [t('limitHistory.balanceBefore', {}, 'Balance before'), amountLabel(player.balanceBefore, player.currency)],
              [t('limitHistory.transactionId', {}, 'Transaction'), transaction.transactionId]
            ]}
          />

          <DetailSection
            title={t('limitHistory.theLimit', {}, 'The limit')}
            rows={[
              [t('limitHistory.source', {}, 'Comes from'), source?.label || limit.limitSource],
              [t('limitHistory.scope', {}, 'Covers'), limit.limitKey || limit.identifier || limit.ruleName],
              [t('limitHistory.configuredMax', {}, 'Configured max'), amountLabel(limit.configuredMax, currency)],
              [t('limitHistory.configuredMin', {}, 'Configured min'), limit.configuredMin ? amountLabel(limit.configuredMin, currency) : null],
              [t('limitHistory.effectiveMax', {}, 'Applied max'), amountLabel(limit.effectiveMax, currency)],
              [t('limitHistory.whyDifferent', {}, 'Adjusted because'), limit.effectiveMaxReason],
              [t('limitHistory.forbidden', {}, 'Blocked amounts'), limit.forbiddenAmounts?.join(', ')],
              [t('limitHistory.lastChangedBy', {}, 'Last changed by'), limit.matchedLimitRecord?.updatedBy],
              [t('limitHistory.lastChangedAt', {}, 'Last changed'), limit.matchedLimitRecord?.updatedAt
                ? new Date(limit.matchedLimitRecord.updatedAt).toLocaleString()
                : null]
            ]}
          />

          <DetailSection
            title={t('limitHistory.whatTheySaw', {}, 'What they were told')}
            rows={[
              [t('limitHistory.playerMessage', {}, 'Player saw'), event.response?.messageToPlayer],
              [t('limitHistory.providerMessage', {}, 'Provider got'), event.response?.messageToProvider],
              [t('limitHistory.alternative', {}, 'Pointed at'), event.response?.suggestedAlternative],
              [t('limitHistory.walletDebited', {}, 'Money moved'), event.response?.walletDebited
                ? t('common.yes', {}, 'Yes')
                : t('limitHistory.noMoneyMoved', {}, 'No — the wallet was never debited')],
              [t('limitHistory.codeBranch', {}, 'Code path'), event.origin?.sourceMarker]
            ]}
          />

          {/* Last resort: the row exactly as it was stored. */}
          <div>
            <button
              type="button"
              onClick={() => setShowRaw((prev) => !prev)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/40 touch-manipulation"
            >
              <CodeBracketIcon className="w-4 h-4" />
              {t('limitHistory.rawEvent', {}, 'Raw event')}
              <ChevronDownIcon className={`w-4 h-4 ml-auto transition-transform ${showRaw ? 'rotate-180' : ''}`} />
            </button>

            {showRaw && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => copy('Event ID', event.eventId)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 text-[11px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/40 touch-manipulation"
                  >
                    <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                    {t('limitHistory.copyId', {}, 'Copy ID')}
                  </button>
                  <button
                    type="button"
                    onClick={() => copy('Event', JSON.stringify(event, null, 2))}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 text-[11px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/40 touch-manipulation"
                  >
                    <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                    {t('limitHistory.copyJson', {}, 'Copy JSON')}
                  </button>
                </div>
                <pre className="max-h-64 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-[10px] leading-relaxed p-3">
                  {JSON.stringify(event, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter sheet
// ---------------------------------------------------------------------------

/** One multi-select row of chips. Picking several within a row means "any of". */
const FilterGroup = ({ title, hint, selected, options, onToggle }) => (
  <section>
    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{title}</p>
    {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{hint}</p>}
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {options.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onToggle(id)}
          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors touch-manipulation ${
            selected.includes(id)
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </section>
);

const FilterSheet = ({ filters, onChange, onClose, t }) => {
  const toggle = (field, value) =>
    onChange({
      ...filters,
      [field]: filters[field].includes(value)
        ? filters[field].filter((entry) => entry !== value)
        : [...filters[field], value]
    });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col animate-slide-up sm:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full sm:hidden" />
          <h3 className="mt-2 sm:mt-0 text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('limitHistory.filters', {}, 'Filters')}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {/* The one cut that changes what an admin does next. */}
          <section>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('limitHistory.whoCanFix', {}, 'Who can fix it')}
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { value: null, label: t('limitHistory.anyFix', {}, 'Any') },
                { value: true, label: t('limitHistory.fixableHere', {}, 'Fixable here') },
                { value: false, label: t('limitHistory.inCodeOnly', {}, 'In code') }
              ].map(({ value, label }) => (
                <button
                  key={String(value)}
                  type="button"
                  onClick={() => onChange({ ...filters, configurable: value })}
                  className={`px-2 py-2.5 rounded-lg text-xs font-medium border transition-colors touch-manipulation ${
                    filters.configurable === value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <FilterGroup
            title={t('limitHistory.reason', {}, 'Why it was refused')}
            selected={filters.breachType}
            onToggle={(id) => toggle('breachType', id)}
            options={Object.entries(BREACH_TYPES).map(([id, meta]) => ({ id, label: meta.label }))}
          />

          <FilterGroup
            title={t('limitHistory.product', {}, 'Product')}
            selected={filters.product}
            onToggle={(id) => toggle('product', id)}
            options={Object.entries(PRODUCTS).map(([id, meta]) => ({ id, label: meta.label }))}
          />

          <FilterGroup
            title={t('limitHistory.provider', {}, 'Where they were playing')}
            selected={filters.provider}
            onToggle={(id) => toggle('provider', id)}
            options={Object.entries(EVENT_PROVIDERS).map(([id, meta]) => ({ id, label: meta.label }))}
          />

          <FilterGroup
            title={t('limitHistory.limitSource', {}, 'Where the limit comes from')}
            hint={t('limitHistory.limitSourceHint', {}, 'The rule that produced the cap')}
            selected={filters.limitSource}
            onToggle={(id) => toggle('limitSource', id)}
            options={Object.entries(LIMIT_SOURCES).map(([id, meta]) => ({ id, label: meta.label }))}
          />
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 shrink-0 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={() => onChange({ ...EMPTY_FILTERS })}
            className="px-4 py-3 text-sm font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 touch-manipulation"
          >
            {t('limitHistory.clearAll', {}, 'Clear')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 touch-manipulation"
          >
            {t('limitHistory.showResults', {}, 'Show results')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

const LimitHistory = ({
  flatUsers = [],
  // Custom leagues live in the shared catalog, and without them an admin-added
  // competition shows up as a bare slug instead of its name and emoji.
  customSportsEvents = [],
  selectedUser,
  onSetLimit,
  onOpenUser
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const [range, setRange] = useState('week');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [scopeToUser, setScopeToUser] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [grouping, setGrouping] = useState('time');
  const [currencyFilter, setCurrencyFilter] = useState(null);

  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const [openEvent, setOpenEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedClusters, setExpandedClusters] = useState({});

  const days = RANGES.find((entry) => entry.id === range)?.days || 7;
  const scopedUsername = scopeToUser && selectedUser ? selectedUser.username : undefined;

  // A user picked on the Limits tab is almost always the reason someone opens
  // the history, so offer that scope — but let it be turned off.
  useEffect(() => {
    setScopeToUser(Boolean(selectedUser));
  }, [selectedUser?.username]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildQuery = useCallback(
    (extra = {}) => ({
      days,
      username: scopedUsername,
      limitOwner: ownerFilter || undefined,
      breachType: filters.breachType,
      limitSource: filters.limitSource,
      product: filters.product,
      provider: filters.provider,
      configurable: filters.configurable === null ? undefined : filters.configurable,
      pageSize: PAGE_SIZE,
      ...extra
    }),
    [days, scopedUsername, ownerFilter, filters]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);

    const result = await fetchLimitEvents(buildQuery());

    if (!result.success) {
      setError(result.error);
      setErrorCode(result.code || null);
      setEvents([]);
      setCursor(null);
      setHasMore(false);
    } else {
      setEvents(result.events);
      setCursor(result.cursor);
      setHasMore(result.hasMore);
    }

    setIsLoading(false);
  }, [buildQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!cursor || isLoadingMore) return;

    setIsLoadingMore(true);
    const result = await fetchLimitEvents(
      buildQuery({
        cursor,
        currency: currencyFilter || undefined
      })
    );

    if (result.success) {
      // The same row can come back if a cursor is replayed, and a duplicated
      // React key is a silent rendering bug rather than a visible one.
      setEvents((prev) => {
        const seen = new Set(prev.map((entry) => entry.eventId));
        return [...prev, ...result.events.filter((entry) => !seen.has(entry.eventId))];
      });
      setCursor(result.cursor);
      setHasMore(result.hasMore);
    } else {
      showNotification('error', t('common.error'), result.error);
    }

    setIsLoadingMore(false);
  };

  // -------------------------------------------------------------------------
  // Search, summary and grouping all run over what is already loaded, so they
  // stay instant while paging happens in the background.
  // -------------------------------------------------------------------------

  // Resolved once per event: the row, the grouping and the search all want it,
  // and it walks the sports catalog to get there.
  const scopes = useMemo(() => {
    const resolved = new Map();
    events.forEach((event) => {
      resolved.set(event.eventId, describeEventScope(event, customSportsEvents));
    });
    return resolved;
  }, [events, customSportsEvents]);

  const currencyPills = useMemo(() => {
    const codes = new Set();
    events.forEach((event) => {
      const code = eventCurrency(event);
      if (code) codes.add(code);
    });
    if (currencyFilter) codes.add(currencyFilter);
    return sortCurrencies([...codes]);
  }, [events, currencyFilter]);

  const currencyCounts = useMemo(() => {
    const counts = {};
    events.forEach((event) => {
      const code = eventCurrency(event);
      if (!code) return;
      counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [events]);

  const visibleEvents = useMemo(() => {
    const byCurrency = currencyFilter
      ? events.filter((event) => eventCurrency(event) === currencyFilter)
      : events;

    const term = search.trim().toLowerCase();
    if (!term) return byCurrency;

    return byCurrency.filter((event) => {
      const scope = scopes.get(event.eventId);
      return [
        scope?.label,
        scope?.detail,
        event.headline,
        event.player?.username,
        event.transaction?.gameName,
        event.transaction?.gameId,
        event.exposure?.matchLabel,
        event.limit?.identifier,
        event.limitOwner,
        eventCurrency(event)
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [events, search, scopes, currencyFilter]);

  const summary = useMemo(() => {
    const players = new Set();
    const owners = new Map();
    let fixable = 0;

    visibleEvents.forEach((event) => {
      if (event.player?.username) players.add(event.player.username);
      if (isFixableInPanel(event)) fixable += 1;
      const key = event.limitOwner || 'unknown';
      owners.set(key, (owners.get(key) || 0) + 1);
    });

    return {
      total: visibleEvents.length,
      players: players.size,
      fixable,
      inCode: visibleEvents.length - fixable,
      topOwners: Array.from(owners.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
    };
  }, [visibleEvents]);

  const groups = useMemo(() => {
    const buckets = new Map();

    const keyFor = (event) => {
      switch (grouping) {
        case 'scope':
          return scopes.get(event.eventId)?.key || 'unknown';
        case 'player':
          return event.player?.username || event.username || 'unknown';
        case 'owner':
          return event.limitOwner || 'unknown';
        case 'reason':
          return event.limit?.breachType || 'above-maximum';
        case 'time':
        default:
          return event.dateBucket || String(event.occurredAt || '').slice(0, 10);
      }
    };

    visibleEvents.forEach((event) => {
      const key = keyFor(event);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(event);
    });

    const labelFor = (key, items) => {
      switch (grouping) {
        case 'scope':
          return scopes.get(items[0].eventId)?.label || key;
        case 'player':
          return `@${key}`;
        case 'owner': {
          const owner = describeOwner(key);
          return owner.isSystem
            ? `${t('limitHistory.inCode', {}, 'In code')}: ${owner.name}`
            : `${getTypeLabel(owner.type)} @${owner.name}`;
        }
        case 'reason':
          return BREACH_TYPES[key]?.label || key;
        case 'time':
        default:
          return formatDayHeading(key);
      }
    };

    const entries = Array.from(buckets.entries()).map(([key, items]) => ({
      key,
      label: labelFor(key, items),
      // The header carries the same glyph as the rows under it.
      scope: grouping === 'scope' ? scopes.get(items[0].eventId) : null,
      events: items,
      items: clusterEventsByTarget(items, (event) => scopes.get(event.eventId), grouping)
    }));

    // Time reads as a timeline; everything else is a ranking, so the biggest
    // offender belongs at the top.
    return grouping === 'time'
      ? entries.sort((a, b) => String(b.key).localeCompare(String(a.key)))
      : entries.sort((a, b) => b.events.length - a.events.length);
  }, [visibleEvents, grouping, scopes, t]);

  useEffect(() => {
    setExpandedClusters({});
  }, [grouping]);

  const activeFilterCount = countActiveFilters(filters) + (ownerFilter ? 1 : 0);

  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setOwnerFilter(null);
    setCurrencyFilter(null);
    setSearch('');
  };

  // -------------------------------------------------------------------------

  return (
    <div className="space-y-3">
      {/* Controls. Sticks under the app bar so the range and the filters stay
          reachable while scrolling a long list. */}
      <div className="sticky top-header z-20 bg-gray-50 dark:bg-gray-900 -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8 px-3 sm:px-4 md:px-6 lg:px-8 pt-1 pb-2 space-y-2">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
          {RANGES.map((entry) => (
            <button
              key={entry.id}
              onClick={() => setRange(entry.id)}
              className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors touch-manipulation ${
                range === entry.id
                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {t(entry.labelKey, {}, entry.labelDefault)}
            </button>
          ))}

          {currencyPills.length > 1 && (
            <>
              <span className="shrink-0 w-px h-4 bg-gray-300 dark:bg-gray-600 mx-0.5" aria-hidden="true" />
              {currencyPills.map((code) => {
                const isActive = currencyFilter === code;
                const count = currencyCounts[code] || 0;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setCurrencyFilter((prev) => (prev === code ? null : code))}
                    title={t('limitHistory.filterCurrency', {}, 'Currency')}
                    className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors touch-manipulation ${
                      isActive
                        ? 'bg-teal-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <span>{code}</span>
                    {count > 0 && (
                      <span className={`tabular-nums ${isActive ? 'text-white/80' : 'text-gray-400 dark:text-gray-500'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}

          {selectedUser && (
            <button
              onClick={() => setScopeToUser((prev) => !prev)}
              className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors touch-manipulation ${
                scopeToUser
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <UserIcon className="w-3.5 h-3.5" />
              @{selectedUser.username}
            </button>
          )}

          {ownerFilter && (
            <button
              onClick={() => setOwnerFilter(null)}
              className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-purple-600 text-white touch-manipulation"
            >
              {describeOwner(ownerFilter).name}
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={load}
            disabled={isLoading}
            className="shrink-0 ml-auto p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 disabled:opacity-50 touch-manipulation"
            title={t('common.refresh', {}, 'Refresh')}
          >
            <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('limitHistory.searchPlaceholder', {}, 'Player, game, match...')}
              className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
              >
                <XMarkIcon className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(true)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors touch-manipulation ${
              activeFilterCount > 0
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'
            }`}
          >
            <FunnelIcon className="w-4 h-4" />
            {activeFilterCount > 0 ? activeFilterCount : t('limitHistory.filter', {}, 'Filter')}
          </button>
        </div>
      </div>

      {/* Loading / error / empty */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : errorCode === 'table.missing' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-200 dark:border-amber-800 p-5 text-center">
          <ExclamationTriangleIcon className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {t('limitHistory.notSetUpTitle', {}, 'Limit history is not switched on yet')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            {t(
              'limitHistory.notSetUpBody',
              {},
              'The wallet records every refused stake, but the table it writes to has to be created first. Run the create-limit-events-table script in the casino repo, then come back.'
            )}
          </p>
          <code className="mt-3 inline-block px-2.5 py-1.5 rounded-lg bg-gray-900 text-gray-100 text-[10px]">
            node scripts/create-limit-events-table.js --apply
          </code>
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800 p-5 text-center">
          <ExclamationTriangleIcon className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{error}</p>
          <button
            onClick={load}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 touch-manipulation"
          >
            {t('common.retry', {}, 'Try again')}
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <InboxIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {t('limitHistory.emptyTitle', {}, 'No bets were refused')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {scopedUsername
              ? t('limitHistory.emptyForUser', {}, 'Nothing was blocked for this account in this period.')
              : t('limitHistory.emptyBody', {}, 'No limit stopped a bet in this period.')}
          </p>
        </div>
      ) : (
        <>
          {/* Summary. An icon and a number each; the two that are also filters
              say so by lighting up, so a number an admin does not like is the
              way into it. */}
          <div className="grid grid-cols-4 gap-2">
            {[
              {
                key: 'total',
                Icon: NoSymbolIcon,
                value: summary.total,
                color: 'text-gray-900 dark:text-gray-100',
                title: t('limitHistory.refusals', {}, 'bets refused')
              },
              {
                key: 'players',
                Icon: UserIcon,
                value: summary.players,
                color: 'text-gray-900 dark:text-gray-100',
                title: t('limitHistory.playersAffected', {}, 'players affected')
              },
              {
                key: 'fixable',
                Icon: WrenchScrewdriverIcon,
                value: summary.fixable,
                color: 'text-emerald-600 dark:text-emerald-400',
                title: t('limitHistory.fixableHere', {}, 'you can fix here'),
                active: filters.configurable === true,
                activeRing: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
                onClick: () =>
                  setFilters((prev) => ({ ...prev, configurable: prev.configurable === true ? null : true }))
              },
              {
                key: 'inCode',
                Icon: CodeBracketIcon,
                value: summary.inCode,
                color: 'text-slate-600 dark:text-slate-300',
                title: t('limitHistory.needDeploy', {}, 'need a deploy'),
                active: filters.configurable === false,
                activeRing: 'border-slate-500 bg-slate-100 dark:bg-slate-800',
                onClick: () =>
                  setFilters((prev) => ({ ...prev, configurable: prev.configurable === false ? null : false }))
              }
            ].map(({ key, Icon, value, color, title, active, activeRing, onClick }) => {
              const Tag = onClick ? 'button' : 'div';
              return (
                <Tag
                  key={key}
                  title={title}
                  {...(onClick ? { onClick, type: 'button' } : {})}
                  className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border py-2.5 transition-colors ${
                    onClick ? 'touch-manipulation' : ''
                  } ${
                    active
                      ? activeRing
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${color} opacity-70`} />
                  <span className={`text-lg font-bold leading-none tabular-nums ${color}`}>{value}</span>
                </Tag>
              );
            })}
          </div>

          {/* Whose limits are doing the refusing. */}
          {summary.topOwners.length > 1 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                {t('limitHistory.topBlockers', {}, 'Refusing the most')}
              </p>
              <div className="space-y-1.5">
                {summary.topOwners.map(([key, count]) => {
                  const owner = describeOwner(key);
                  const share = Math.round((count / Math.max(summary.total, 1)) * 100);
                  return (
                    <button
                      key={key}
                      onClick={() => setOwnerFilter(ownerFilter === key ? null : key)}
                      className="w-full flex items-center gap-2 touch-manipulation"
                    >
                      <span className="w-24 sm:w-32 shrink-0 truncate text-left text-[11px] text-gray-700 dark:text-gray-300">
                        {owner.isSystem ? `⚙ ${owner.name}` : `@${owner.name}`}
                      </span>
                      <span className="flex-1 min-w-0 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <span
                          className={`block h-full rounded-full ${owner.isSystem ? 'bg-slate-400' : 'bg-blue-500'}`}
                          style={{ width: `${Math.max(share, 3)}%` }}
                        />
                      </span>
                      <span className="shrink-0 w-8 text-right text-[11px] font-semibold text-gray-600 dark:text-gray-400 tabular-nums">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* How the list is cut up. */}
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
              {t('limitHistory.groupBy', {}, 'Group by')}
            </span>
            {GROUPINGS.map((entry) => {
              const GroupIcon = entry.Icon;
              return (
                <button
                  key={entry.id}
                  onClick={() => setGrouping(entry.id)}
                  className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full transition-colors touch-manipulation ${
                    grouping === entry.id
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <GroupIcon className="w-3.5 h-3.5" />
                  {t(entry.labelKey, {}, entry.labelDefault)}
                </button>
              );
            })}
          </div>

          {visibleEvents.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <FunnelIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('limitHistory.noMatches', {}, 'Nothing matches what you searched for')}
              </p>
              <button
                onClick={resetFilters}
                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline touch-manipulation"
              >
                {t('limits.clearFilters', {}, 'Clear filters')}
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {groups.map((group) => {
                const isOpen = collapsedGroups[group.key] !== true;
                return (
                  <section
                    key={group.key}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsedGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))
                      }
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 text-left touch-manipulation"
                    >
                      {group.scope && <ScopeGlyph scope={group.scope} />}
                      {grouping === 'player' && (() => {
                        const node = flatUsers.find((user) => user.username === group.key);
                        const Icon = getTypeIcon(node?.type || 'player');
                        return (
                          <span className={`shrink-0 p-1 rounded-md ${getTypeColor(node?.type || 'player')}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                        );
                      })()}
                      <span className="flex-1 min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {group.label}
                      </span>
                      <span className="shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                        {group.events.length}
                      </span>
                      <ChevronDownIcon
                        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isOpen && (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {group.items.map((item) => {
                          if (item.type === 'single') {
                            return (
                              <EventRow
                                key={item.event.eventId}
                                event={item.event}
                                scope={scopes.get(item.event.eventId)}
                                onOpen={setOpenEvent}
                                t={t}
                              />
                            );
                          }

                          const clusterId = `${group.key}::${item.key}`;
                          const onlyStack = group.items.length === 1;
                          const clusterOpen =
                            clusterId in expandedClusters ? expandedClusters[clusterId] : onlyStack;

                          return (
                            <ClusterBlock
                              key={clusterId}
                              cluster={item}
                              scopes={scopes}
                              grouping={grouping}
                              isOpen={clusterOpen}
                              onToggle={() =>
                                setExpandedClusters((prev) => ({
                                  ...prev,
                                  [clusterId]: !clusterOpen
                                }))
                              }
                              onOpenEvent={setOpenEvent}
                              flatUsers={flatUsers}
                              t={t}
                            />
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}

              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="w-full py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/40 disabled:opacity-50 inline-flex items-center justify-center gap-2 touch-manipulation"
                >
                  {isLoadingMore ? (
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                  {t('limitHistory.loadMore', {}, 'Load older refusals')}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {showFilters && (
        <FilterSheet
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
          t={t}
        />
      )}

      {openEvent && (
        <EventDetail
          event={openEvent}
          scope={scopes.get(openEvent.eventId)}
          flatUsers={flatUsers}
          t={t}
          showNotification={showNotification}
          onClose={() => setOpenEvent(null)}
          onSetLimit={(event, targetUsername) => {
            setOpenEvent(null);
            onSetLimit?.(event, targetUsername);
          }}
          onOpenUser={(username) => {
            setOpenEvent(null);
            onOpenUser?.(username);
          }}
        />
      )}
    </div>
  );
};

export default LimitHistory;
